import React, { useState } from 'react'
import {
  createStyles,
  withStyles,
  Alert,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  ActiveSwitch,
  Tab,
  Tabs,
  Theme as ThemeOverride,
  useClientOptions,
  useDialogs
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import type { PresentationSection, QuoteDocument } from 'paintscout'
import { getObjectLabels } from '@paintscout/util/builder'

import isEqual from 'lodash/isEqual'
import { QuoteContextProvider } from '../../../context'
import PresentationSectionFormik from '../../PresentationSectionFormik'
import sections from '../../sections'
import AdvancedOptions from './AdvancedOptions'
import ImportExportOptions from './ImportExportOptions'
import BackgroundOptions from './BackgroundOptions'
import PresentationSectionPreview from './PresentationSectionPreview'
import EmptySectionAlerts from './EmptySectionAlerts'

export interface EditPresentationSectionDialogProps extends Omit<DialogProps, 'message' | 'title' | 'loading'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  isTemplate?: boolean
  isCustom?: boolean
  section: PresentationSection
  isSettings?: boolean
  showAdvanced?: boolean
  quote?: QuoteDocument
  onConfirm?: (section: PresentationSection, isDirty?: boolean) => void
  onCancel?: () => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    alert: {
      margin: theme.spacing(2)
    },
    paper: {
      // height: '80vh',
      maxWidth: '1250px'
    },
    dialogContent: {
      padding: 0,
      paddingBottom: theme.spacing(3),
      display: 'flex',
      '& #menu-bar': {
        top: 0
      }
    },
    previewDialogContent: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%'
    },
    formWrapper: {
      width: '50%',
      padding: theme.spacing(2),
      [theme.breakpoints.down('xl')]: {
        width: '100%'
      }
    },
    preview: {
      width: '50%',
      padding: theme.spacing(2),
      borderLeft: `1px solid #ddd`
    },
    tabs: {
      marginBottom: theme.spacing(3)
    },
    tab: {
      padding: theme.spacing(1, 0)
    },
    tabsWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.spacing(2.5)
    },
    scaled: {
      display: 'flex',
      alignItems: 'center'
    },
    scalerWrapper: {
      paddingBottom: theme.spacing(3),
      maxWidth: '100%'
    },
    helpWrapper: {
      marginBottom: theme.spacing(2)
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    switchLabel: {
      marginLeft: '0 !important',
      marginRight: 0
    }
  })

function EditPresentationSectionDialog({
  isCustom,
  isTemplate,
  open,
  classes,
  onConfirm,
  onCancel,
  showAdvanced,
  isSettings,
  quote,
  ...props
}: EditPresentationSectionDialogProps) {
  // const [section, setSection] = useState<PresentationSection>(props.section)
  const { dismissAllDialogs, openDialog, dismissDialog } = useDialogs()
  const section = props.section
  const isPreviewSection = section.type.includes('sherwin-williams') || section.type.includes('benjamin-moore')
  const [active, setActive] = useState<boolean>(section.active ?? true)

  const [tabValue, setTabValue] = useState(isPreviewSection ? 'Section' : 'Content')
  const [rightTabValue, setRightTabValue] = useState('Preview')

  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })

  const FormComponent = sections?.[section.type]?.Form
  const BackgroundOptionsComponent = sections?.[section.type]?.BackgroundOptions ?? BackgroundOptions
  const HelpComponent = sections?.[section.type]?.Help

  const tabs = ['Content', 'Style', 'Advanced', 'Import/Export'].filter((tab) => showAdvanced || tab === 'Content')
  const rightTabs = ['Preview', HelpComponent ? 'Help' : null].filter(Boolean)
  const baseSection = { ...section, active: section.active ?? true }

  return (
    <QuoteContextProvider quote={quote} tableView={'area'}>
      <PresentationSectionFormik
        section={section}
        onSubmit={(values, _actions) => {
          const updatedSection = { ...values.section, active }
          const isDirty = !isEqual(updatedSection, baseSection)
          if (isDirty) {
            // Set presentation as custom if we have updated section.
            updatedSection.custom = true
          }
          onConfirm(updatedSection, isDirty)
        }}
      >
        {({ submitForm, values }) => {
          const hasChanges = () => {
            const updatedSection = { ...(values.section ?? section), active }
            return !isEqual(updatedSection, baseSection)
          }

          const handleClose = () => {
            if (hasChanges()) {
              openDialog(ConfirmationDialog, {
                message: 'You have unsaved changes. Are you sure you want to close?',
                onConfirm: () => {
                  dismissDialog()
                  onCancel?.()
                  dismissAllDialogs()
                },
                onCancel: () => {
                  dismissDialog()
                }
              })
            } else {
              onCancel?.()
              dismissAllDialogs()
            }
          }

          return (
            <Dialog
              open={open}
              onClose={handleClose}
              fullWidth={true}
              maxWidth={'lg'}
              classes={{ paper: classes.paper }}
              {...props}
            >
              <DialogTitle
                classes={{ root: classes.dialogTitle }}
                rightContent={
                  <IconButton size="small" onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                }
                subtitle={isPreviewSection ? section.label : undefined}
              >
                {isPreviewSection ? 'Preview Section' : `Edit ${section.label} Section`}
              </DialogTitle>
              {isTemplate && !isCustom && (
                <Grid container spacing={1}>
                  <Grid item xs={12} classes={{ root: classes.alert }}>
                    <Alert severity={'warning'}>
                      Customizing Presentation sections will cause new {objectLabels.quote.plural.toLowerCase()} to use
                      this customized Presentation.
                    </Alert>
                  </Grid>
                </Grid>
              )}
              {isTemplate && isCustom && (
                <Grid container spacing={1}>
                  <Grid item xs={12} classes={{ root: classes.alert }}>
                    <Alert severity={'warning'}>
                      This Presentation has been customized from the selected Presentation template. New{' '}
                      {objectLabels.quote.plural.toLowerCase()} will use this customized Presentation.
                    </Alert>
                  </Grid>
                </Grid>
              )}
              {/* Dialog content for preview vs normal sections */}
              {isPreviewSection ? (
                <DialogContent classes={{ root: classes.previewDialogContent }}>
                  <SelectedTab selectedTab={rightTabValue} />
                </DialogContent>
              ) : (
                <DialogContent classes={{ root: classes.dialogContent }}>
                  <Hidden lgDown={true}>
                    <div className={classes.formWrapper}>
                      <Tabs
                        classes={{ root: classes.tabs }}
                        value={tabValue}
                        onChange={(ev, v) => {
                          setTabValue(v)
                        }}
                      >
                        {tabs.map((tab) => (
                          <Tab key={tab} value={tab} label={tab} />
                        ))}
                      </Tabs>
                      <SelectedTab selectedTab={tabValue} />
                    </div>

                    <div className={classes.preview}>
                      <Tabs
                        classes={{ root: classes.tabs }}
                        value={rightTabValue}
                        onChange={(ev, v) => {
                          setRightTabValue(v)
                        }}
                      >
                        {rightTabs.map((tab) => (
                          <Tab key={tab} value={tab} label={tab} />
                        ))}
                      </Tabs>
                      <SelectedTab selectedTab={rightTabValue} />
                    </div>
                  </Hidden>
                  <Hidden xlUp={true}>
                    <div className={classes.formWrapper}>
                      <Tabs
                        classes={{ root: classes.tabs }}
                        value={tabValue}
                        onChange={(ev, v) => {
                          setTabValue(v)
                        }}
                      >
                        {[...tabs, ...rightTabs].map((tab) => (
                          <Tab key={tab} value={tab} label={tab} />
                        ))}
                      </Tabs>
                      <SelectedTab selectedTab={tabValue} />
                    </div>
                  </Hidden>
                </DialogContent>
              )}
              <DialogActions
                showBorder={true}
                leftButton={
                  !isPreviewSection ? (
                    <FormControlLabel
                      className={classes.switchLabel}
                      control={<ActiveSwitch color="primary" checked={active} onChange={handleActiveToggle} />}
                      label="Active"
                      labelPlacement="end"
                    />
                  ) : undefined
                }
              >
                <Button onClick={submitForm} variant={'contained'}>
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          )
        }}
      </PresentationSectionFormik>
    </QuoteContextProvider>
  )

  function handleActiveToggle(_event: React.ChangeEvent<HTMLElement>, checked: boolean) {
    setActive(checked)
  }

  function SelectedTab({ selectedTab }) {
    switch (selectedTab) {
      case 'Content':
        return <FormComponent isSettings={isSettings} />

      case 'Style':
        return <BackgroundOptionsComponent />
      case 'Advanced':
        return <AdvancedOptions />
      case 'Import/Export':
        return <ImportExportOptions />
      case 'Preview':
        return (
          <ThemeOverride customerFacing>
            <EmptySectionAlerts />
            <div className={classes.scalerWrapper}>
              <PresentationSectionPreview editFromDialog={true} />
            </div>
          </ThemeOverride>
        )
      case 'Help':
        return (
          <ThemeOverride customerFacing>
            <div className={classes.helpWrapper}>
              <HelpComponent section={section} />
            </div>
            <div className={classes.scalerWrapper}>
              <PresentationSectionPreview showHelp={true} isSettings={true} />
            </div>
          </ThemeOverride>
        )
      default:
        return null
    }
  }
}

export default withStyles(styles)(EditPresentationSectionDialog)
